import React, { useState, useEffect, useMemo } from 'react'
import {
  assetApi,
  locationApi,
  organizationApi,
  iotControllerApi,
  departmentApi
} from '../api/services'
import { useAuth } from '../context/AuthContext'
import { useSensorData } from '../context/SensorDataContext'

const CTRL_ONLINE = new Set(['online', 'live', 'interval', 'warning'])

export default function AssetManagement () {
  const { activeTenant, user } = useAuth()
  const { livePayloads } = useSensorData()
  // Extract numeric tenant id whether activeTenant is an object {id,name} or a raw number
  const resolvedTenant =
    (typeof activeTenant === 'object' ? activeTenant?.id : activeTenant) ??
    user?.tenant_id

  const [assets, setAssets] = useState([])
  const [controllers, setControllers] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)

  // Org hierarchy state
  const [orgTree, setOrgTree] = useState([]) // nested org tree
  const [flatOrgs, setFlatOrgs] = useState([]) // flat list for lookups

  // Location state (flat list for lookups, used after org is selected)
  const [flatLocations, setFlatLocations] = useState([])

  // Asset placement selection: Organization › Site › Department
  const [selectedOrgId, setSelectedOrgId] = useState('')
  const [selectedLocId, setSelectedLocId] = useState('') // chosen Site (location id)

  // Departments
  const [allDepartments, setAllDepartments] = useState([])
  const [selectedDeptId, setSelectedDeptId] = useState('')

  const [activeTab, setActiveTab] = useState('All')
  const [searchQuery, setSearchQuery] = useState('')

  const [isFormModalOpen, setIsFormModalOpen] = useState(false)
  const [editingId, setEditingId] = useState(null)

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [assetToDelete, setAssetToDelete] = useState(null)

  const [toast, setToast] = useState({
    visible: false,
    message: '',
    type: 'success'
  })

  const initialFormState = {
    name: '',
    asset_type: '',
    location_id: '',
    status: 'Active',
    placement: '',
    next_maintenance_date: ''
  }
  const [formData, setFormData] = useState(initialFormState)

  // HELPER FUNCTIONS

  const showToast = (message, type = 'success') => {
    setToast({ visible: true, message, type })
    setTimeout(() => {
      setToast({ visible: false, message: '', type: 'success' })
    }, 3000)
  }

  const handleInputChange = e => {
    const value =
      e.target.name === 'location_id'
        ? parseInt(e.target.value)
        : e.target.value

    setFormData({ ...formData, [e.target.name]: value })
  }

  // API CALLS

  const fetchAssets = async () => {
    try {
      setIsLoading(true)

      if (!resolvedTenant) {
        showToast('No tenant found. Please log in again.', 'error')
        return
      }

      const [assetRes, ctrlRes] = await Promise.all([
        assetApi.getAll({ tenantId: resolvedTenant }),
        iotControllerApi.getAll(resolvedTenant)
      ])
      setAssets(assetRes.data?.data || assetRes.data || [])
      const ctrlData = ctrlRes.data?.data || ctrlRes.data || []
      setControllers(Array.isArray(ctrlData) ? ctrlData : [])
    } catch (err) {
      setError('Failed to load assets.')
      showToast('Failed to load data from server.', 'error')
    } finally {
      setIsLoading(false)
    }
  }

  // asset_id → true if any linked controller is online (live or stored status)
  const assetOnlineMap = useMemo(() => {
    const map = {}
    controllers.forEach(ctrl => {
      const liveStatus = (
        livePayloads[ctrl.id]?.status ||
        ctrl.status ||
        ''
      ).toLowerCase()
      const isOn = CTRL_ONLINE.has(liveStatus)
      ctrl.asset_ids?.forEach(aid => {
        if (isOn) map[aid] = true
        else if (map[aid] === undefined) map[aid] = false
      })
    })
    return map
  }, [controllers, livePayloads])

  // Derive effective display status for an asset
  const effectiveStatus = asset => {
    const s = (asset.status || '').toLowerCase()
    // Keep operational statuses as-is
    if (s === 'maintenance' || s === 'fault' || s === 'inactive')
      return asset.status
    // For active/unknown: reflect controller connection
    const isLinked = controllers.some(
      c =>
        c.asset_ids?.includes(asset.id) ||
        c.asset_ids?.includes(String(asset.id))
    )
    if (!isLinked) return 'offline'
    return assetOnlineMap[asset.id] === true ? 'online' : 'offline'
  }

  const fetchOrgAndLocations = async () => {
    try {
      if (!resolvedTenant) return
      const [orgRes, locRes, deptRes] = await Promise.all([
        organizationApi.getAll({ tenantId: resolvedTenant }),
        locationApi.getAll({ tenantId: resolvedTenant }),
        departmentApi
          .getAll({ tenantId: resolvedTenant })
          .catch(() => ({ data: [] }))
      ])
      const tree = orgRes.data?.data || orgRes.data || []
      setOrgTree(tree)
      setFlatOrgs(buildFlatOrgs(tree))

      const locTree = locRes.data?.data || locRes.data || []
      setFlatLocations(buildFlatLocs(locTree))

      const depts = deptRes.data?.data || deptRes.data || []
      setAllDepartments(Array.isArray(depts) ? depts : [])
    } catch (err) {
      console.error('Failed to fetch org/locations', err)
    }
  }

  useEffect(() => {
    fetchAssets()
    fetchOrgAndLocations()
  }, [resolvedTenant]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Flatten helpers ────────────────────────────────────────────────────────

  /** Flatten org tree into a lookup array with parent_id. */
  const buildFlatOrgs = nodes => {
    let flat = []
    const walk = list => {
      list.forEach(n => {
        flat.push({
          id: String(n.id),
          name: n.name,
          parent_id: n.parent_id != null ? String(n.parent_id) : null
        })
        if (n.children?.length) walk(n.children)
      })
    }
    walk(nodes)
    return flat
  }

  /** Flatten location tree into a lookup array with parent_id and org_id. */
  const buildFlatLocs = nodes => {
    let flat = []
    const walk = list => {
      list.forEach(n => {
        flat.push({
          id: String(n.id),
          name: n.name,
          parent_id: n.parent_id != null ? String(n.parent_id) : null,
          org_id:
            n.org_id != null
              ? String(n.org_id)
              : n.organization_id != null
              ? String(n.organization_id)
              : null
        })
        if (n.children?.length) walk(n.children)
      })
    }
    walk(nodes)
    return flat
  }

  // ── Placement selection: Organization › Site › Department ──────────────────

  // Organizations the user can place an asset under (no nesting in this model)
  const orgOptions = useMemo(
    () => flatOrgs.filter(o => o.parent_id == null),
    [flatOrgs]
  )

  // Sites belonging to the chosen organization
  const siteOptions = useMemo(
    () =>
      selectedOrgId
        ? flatLocations.filter(
            l =>
              String(l.org_id) === String(selectedOrgId) && l.parent_id == null
          )
        : [],
    [flatLocations, selectedOrgId]
  )

  const effectiveLocationId = selectedLocId
    ? parseInt(selectedLocId, 10) || null
    : null

  // Departments inside the chosen site
  const deptOptions = useMemo(
    () =>
      effectiveLocationId
        ? allDepartments.filter(
            d => String(d.location_id) === String(effectiveLocationId)
          )
        : [],
    [allDepartments, effectiveLocationId]
  )

  const resetCascade = () => {
    setSelectedOrgId(orgOptions.length === 1 ? String(orgOptions[0].id) : '')
    setSelectedLocId('')
    setSelectedDeptId('')
  }

  // Auto-select the organization when there is only one
  useEffect(() => {
    if (orgOptions.length === 1 && !selectedOrgId)
      setSelectedOrgId(String(orgOptions[0].id))
  }, [orgOptions, selectedOrgId]) // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-select the site when the organization has exactly one
  useEffect(() => {
    if (siteOptions.length === 1 && !selectedLocId)
      setSelectedLocId(String(siteOptions[0].id))
  }, [siteOptions, selectedLocId]) // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-select the department when the site has exactly one
  useEffect(() => {
    if (deptOptions.length === 1 && !selectedDeptId)
      setSelectedDeptId(String(deptOptions[0].id))
  }, [deptOptions, selectedDeptId]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Edit-mode path reconstruction ─────────────────────────────────────────

  const handleSubmit = async e => {
    e.preventDefault()

    if (!resolvedTenant) {
      showToast('No tenant found.', 'error')
      return
    }

    if (!effectiveLocationId) {
      showToast('Please select a site.', 'error')
      return
    }

    const payload = {
      ...formData,
      location_id: effectiveLocationId,
      department_id: selectedDeptId ? parseInt(selectedDeptId, 10) : null,
      status:
        formData.status.toLowerCase() === 'faulty'
          ? 'fault'
          : formData.status.toLowerCase()
    }

    try {
      if (editingId) {
        const response = await assetApi.update(
          editingId,
          payload,
          resolvedTenant
        )
        const updated = response.data?.data || response.data
        setAssets(
          assets.map(asset => (asset.id === editingId ? updated : asset))
        )
        showToast('Asset updated successfully!')
      } else {
        const response = await assetApi.create(payload, resolvedTenant)
        const created = response.data?.data || response.data
        setAssets([...assets, created])
        showToast('New asset added successfully!')
      }

      setIsFormModalOpen(false)
      setFormData(initialFormState)
      setEditingId(null)
      resetCascade()
    } catch (err) {
      console.error(err)
      showToast(err.response?.data?.message || 'Failed to save asset.', 'error')
    }
  }

  const handleToggleActive = async asset => {
    const next = asset.is_active === false // currently inactive → activating

    // Optimistic update
    setAssets(prev =>
      prev.map(a => (a.id === asset.id ? { ...a, is_active: next } : a))
    )
    try {
      const res = await assetApi.setActive(asset.id, next, resolvedTenant)
      const updated = res.data?.data || res.data
      setAssets(prev =>
        prev.map(a => (a.id === asset.id ? { ...a, ...updated } : a))
      )
      showToast(res.data?.message || (next ? 'Asset activated' : 'Asset deactivated'))
      // Linked controllers may have changed — refresh them for the online map
      fetchAssets()
    } catch (err) {
      showToast(
        err.response?.data?.message || 'Failed to update asset status',
        'error'
      )
      // Revert on failure
      setAssets(prev =>
        prev.map(a => (a.id === asset.id ? { ...a, is_active: !next } : a))
      )
    }
  }

  const initiateDelete = asset => {
    setAssetToDelete(asset)
    setIsDeleteModalOpen(true)
  }

  const confirmDelete = async () => {
    if (!assetToDelete) return
    try {
      await assetApi.delete(assetToDelete.id, resolvedTenant)
      setAssets(assets.filter(asset => asset.id !== assetToDelete.id))
      showToast(`${assetToDelete.name} deleted successfully.`, 'success')
    } catch (err) {
      console.error('Error deleting asset:', err)
      showToast('Failed to delete asset.', 'error')
    } finally {
      setIsDeleteModalOpen(false)
      setAssetToDelete(null)
    }
  }

  // BUTTON CLICKS & FILTERING

  const handleAddClick = () => {
    setEditingId(null)
    setFormData(initialFormState)
    resetCascade()
    setIsFormModalOpen(true)
  }

  const handleEditClick = asset => {
    setEditingId(asset.id)
    setFormData({
      name: asset.name,
      asset_type: asset.asset_type,
      location_id: asset.location?.id ?? '',
      status:
        asset.status === 'fault'
          ? 'Faulty'
          : asset.status
          ? asset.status.charAt(0).toUpperCase() + asset.status.slice(1)
          : 'Active',
      placement: asset.placement || '',
      next_maintenance_date: asset.next_maintenance_date || ''
    })

    const locId = asset.location?.id
    const locEntry = locId
      ? flatLocations.find(l => String(l.id) === String(locId))
      : null
    setSelectedOrgId(locEntry?.org_id != null ? String(locEntry.org_id) : '')
    setSelectedLocId(locId ? String(locId) : '')
    setSelectedDeptId(asset.department_id ? String(asset.department_id) : '')

    setIsFormModalOpen(true)
  }

  const filteredAssets = assets.filter(asset => {
    const assetName = asset.name || ''
    const matchesSearch = assetName
      .toLowerCase()
      .includes(searchQuery.toLowerCase())

    let statusToMatch = activeTab.toLowerCase()
    if (statusToMatch === 'faulty') statusToMatch = 'fault'

    const assetStatus = (asset.status || '').toLowerCase()
    const matchesTab = activeTab === 'All' || assetStatus === statusToMatch

    return matchesSearch && matchesTab
  })

  const getStatusBadge = status => {
    switch ((status || '').toLowerCase()) {
      case 'online':
        return (
          <span className='px-3 py-1 text-xs border border-green-400 text-green-600 bg-green-50 rounded-full flex items-center gap-1 w-fit'>
            <span className='w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse inline-block' />
            Online
          </span>
        )
      case 'offline':
        return (
          <span className='px-3 py-1 text-xs border border-gray-300 text-gray-500 bg-gray-50 rounded-full flex items-center gap-1 w-fit'>
            <span className='w-1.5 h-1.5 rounded-full bg-gray-400 inline-block' />
            Offline
          </span>
        )
      case 'active':
        return (
          <span className='px-3 py-1 text-xs border border-green-400 text-green-600 bg-green-50 rounded-full'>
            Active
          </span>
        )
      case 'live':
        return (
          <span className='px-3 py-1 text-xs border border-emerald-400 text-emerald-600 bg-emerald-50 rounded-full'>
            Live
          </span>
        )
      case 'fault':
        return (
          <span className='px-3 py-1 text-xs border border-red-400 text-red-600 bg-red-50 rounded-full'>
            Faulty
          </span>
        )
      case 'maintenance':
        return (
          <span className='px-3 py-1 text-xs border border-orange-400 text-orange-600 bg-orange-50 rounded-full'>
            Maintenance
          </span>
        )
      case 'inactive':
        return (
          <span className='px-3 py-1 text-xs border border-gray-400 text-gray-500 bg-gray-50 rounded-full'>
            Inactive
          </span>
        )
      default:
        return (
          <span className='px-3 py-1 text-xs border border-gray-400 text-gray-600 bg-gray-50 rounded-full'>
            {status}
          </span>
        )
    }
  }

  if (isLoading)
    return (
      <div className='p-8 text-center text-gray-500 flex justify-center items-center min-h-screen'>
        Loading assets...
      </div>
    )

  return (
    <div className='p-8 bg-[#f8fafc] min-h-screen font-sans relative'>
      {/* Header */}
      <div className='flex justify-between items-center mb-6'>
        <div>
          <h1 className='text-2xl font-bold text-gray-800'>Asset Management</h1>
          <p className='text-sm text-gray-500 mt-1'>
            Manage and monitor all industrial assets
          </p>
        </div>
        <button
          onClick={handleAddClick}
          className='bg-[#1048b3] text-white px-4 py-2 rounded-md hover:bg-blue-800 transition flex items-center gap-2 text-sm font-medium'
        >
          <span>+</span> Add Asset
        </button>
      </div>

      {/* Tabs and Search Bar */}
      <div className='flex justify-between items-center mb-8'>
        <div className='flex space-x-1 bg-white border border-gray-100 p-1 rounded-lg shadow-sm'>
          {['All', 'Active', 'Maintenance', 'Faulty'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === tab
                  ? 'bg-[#1048b3] text-white'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>
        <div className='relative'>
          <svg
            className='w-4 h-4 absolute left-3 top-3 text-gray-400'
            fill='none'
            stroke='currentColor'
            viewBox='0 0 24 24'
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth='2'
              d='M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z'
            />
          </svg>
          <input
            type='text'
            placeholder='Search cameras by names'
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className='pl-10 pr-4 py-2 border border-gray-200 rounded-md w-72 text-sm focus:outline-none focus:border-blue-500'
          />
        </div>
      </div>

      {/* Table */}
      <div className='bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden'>
        <table className='w-full text-left border-collapse'>
          <thead className='bg-[#f1f5f9] text-gray-600 text-sm'>
            <tr>
              <th className='p-4 font-semibold'>Asset</th>
              <th className='p-4 font-semibold'>Type</th>
              <th className='p-4 font-semibold'>Site</th>
              <th className='p-4 font-semibold'>Status</th>
              <th className='p-4 font-semibold'>Next Maintenance</th>
              <th className='p-4 font-semibold'>Actions</th>
            </tr>
          </thead>
          <tbody className='text-sm text-gray-700'>
            {filteredAssets.map(asset => (
              <tr
                key={asset.id}
                className='border-b border-gray-100 hover:bg-gray-50'
              >
                <td className='p-4'>
                  <div className='font-semibold text-gray-800'>
                    {asset.name}
                  </div>
                  <div className='text-xs text-gray-400 mt-0.5 font-mono'>
                    {asset.id}
                  </div>
                </td>
                <td className='p-4'>{asset.asset_type}</td>
                <td className='p-4 flex items-center gap-2 mt-2'>
                  <svg
                    className='w-4 h-4 text-gray-400'
                    fill='currentColor'
                    viewBox='0 0 20 20'
                  >
                    <path
                      fillRule='evenodd'
                      d='M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z'
                      clipRule='evenodd'
                    />
                  </svg>
                  <div>
                    <div>{asset.location?.name || 'Unassigned'}</div>
                    {asset.department?.name && (
                      <div className='text-xs text-indigo-500 mt-0.5'>
                        {asset.department.name}
                      </div>
                    )}
                    {asset.placement && (
                      <div className='text-xs text-gray-400 mt-0.5'>
                       {asset.placement}
                      </div>
                    )}
                  </div>
                </td>
                <td className='p-4'>
                  {asset.is_active === false
                    ? getStatusBadge('inactive')
                    : getStatusBadge(effectiveStatus(asset))}
                </td>
                <td className='p-4'>{asset.next_maintenance_date}</td>

                <td className='p-4'>
                  <div className='flex items-center gap-4'>
                    {/* Active / Inactive toggle */}
                    <button
                      onClick={() => handleToggleActive(asset)}
                      title={
                        asset.is_active === false
                          ? 'Activate asset'
                          : 'Deactivate asset'
                      }
                      className='flex items-center'
                    >
                      <span
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                          asset.is_active === false ? 'bg-gray-300' : 'bg-green-500'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                            asset.is_active === false
                              ? 'translate-x-1'
                              : 'translate-x-4'
                          }`}
                        />
                      </span>
                    </button>

                    <button
                      onClick={() => handleEditClick(asset)}
                      className='text-gray-500 hover:text-blue-600 transition-colors'
                      title='Edit Asset'
                    >
                      <svg
                        width='18'
                        height='18'
                        viewBox='0 0 24 24'
                        fill='none'
                        stroke='currentColor'
                        strokeWidth='1.5'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      >
                        <line x1='4' y1='21' x2='4' y2='14'></line>
                        <line x1='4' y1='10' x2='4' y2='3'></line>
                        <line x1='12' y1='21' x2='12' y2='12'></line>
                        <line x1='12' y1='8' x2='12' y2='3'></line>
                        <line x1='20' y1='21' x2='20' y2='16'></line>
                        <line x1='20' y1='12' x2='20' y2='3'></line>
                        <line x1='1' y1='14' x2='7' y2='14'></line>
                        <line x1='9' y1='8' x2='15' y2='8'></line>
                        <line x1='17' y1='16' x2='23' y2='16'></line>
                      </svg>
                    </button>

                    <button
                      onClick={() => initiateDelete(asset)}
                      className='text-[#ff6b6b] hover:text-red-700 transition-colors'
                      title='Delete Asset'
                    >
                      <svg
                        width='18'
                        height='18'
                        viewBox='0 0 24 24'
                        fill='none'
                        stroke='currentColor'
                        strokeWidth='1.5'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      >
                        <polyline points='3 6 5 6 21 6'></polyline>
                        <path d='M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2'></path>
                        <line x1='10' y1='11' x2='10' y2='17'></line>
                        <line x1='14' y1='11' x2='14' y2='17'></line>
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* =========================================
          1. ADD / EDIT ASSET MODAL
          ========================================= */}
      {isFormModalOpen && (
        <div className='fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-40'>
          <div className='bg-white rounded-xl w-[420px] shadow-2xl max-h-[90vh] flex flex-col overflow-hidden'>
            {/* 🔥 HEADER (STICKY + CLOSE BUTTON) */}
            <div className='flex justify-between items-center px-6 py-4 border-b sticky top-0 bg-white z-10'>
              <h2 className='text-[20px] font-bold text-[#1e293b]'>
                {editingId ? 'Edit Asset' : 'Add New Asset'}
              </h2>

              <button
                onClick={() => setIsFormModalOpen(false)}
                className='text-gray-400 hover:text-red-500 text-lg font-bold'
              >
                ✕
              </button>
            </div>

            <div className='overflow-y-auto px-6 py-5'>
              <form onSubmit={handleSubmit} className='flex flex-col gap-5'>
                {/* Asset Name */}
                <div>
                  <label className='block text-[13px] font-semibold text-[#334155] mb-1.5'>
                    Asset Name
                  </label>
                  <input
                    required
                    type='text'
                    name='name'
                    value={formData.name}
                    onChange={handleInputChange}
                    className='w-full border border-gray-300 p-2.5 rounded-md focus:ring-1 focus:ring-blue-600 outline-none text-sm'
                  />
                </div>

                {/* Type */}
                <div>
                  <label className='block text-[13px] font-semibold text-[#334155] mb-1.5'>
                    Type (e.g., CNC Machine)
                  </label>
                  <input
                    required
                    type='text'
                    name='asset_type'
                    value={formData.asset_type}
                    onChange={handleInputChange}
                    className='w-full border border-gray-300 p-2.5 rounded-md focus:ring-1 focus:ring-blue-600 outline-none text-sm'
                  />
                </div>

                {/* ================= ORGANIZATION › SITE › DEPARTMENT ================= */}
                {orgOptions.length === 0 ? (
                  <p className='text-xs text-orange-500 border border-orange-200 bg-orange-50 rounded-md p-2.5'>
                    No organizations found. Create one in Organization management
                    first.
                  </p>
                ) : (
                  <>
                    {/* Organization — shown only when there is more than one */}
                    {orgOptions.length > 1 && (
                      <div>
                        <label className='block text-[13px] font-semibold text-[#334155] mb-1.5'>
                          Organization
                        </label>
                        <select
                          value={selectedOrgId}
                          onChange={e => {
                            setSelectedOrgId(e.target.value)
                            setSelectedLocId('')
                            setSelectedDeptId('')
                          }}
                          className='w-full border border-gray-300 p-2.5 rounded-md focus:ring-1 focus:ring-blue-600 outline-none text-sm bg-white'
                        >
                          <option value=''>Select organization</option>
                          {orgOptions.map(o => (
                            <option key={o.id} value={String(o.id)}>
                              {o.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    {/* Site */}
                    <div>
                      <label className='block text-[13px] font-semibold text-[#334155] mb-1.5'>
                        Site
                      </label>
                      <select
                        value={selectedLocId}
                        onChange={e => {
                          setSelectedLocId(e.target.value)
                          setSelectedDeptId('')
                        }}
                        disabled={!selectedOrgId}
                        className='w-full border border-gray-300 p-2.5 rounded-md focus:ring-1 focus:ring-blue-600 outline-none text-sm bg-white disabled:bg-gray-50 disabled:text-gray-400'
                      >
                        <option value=''>
                          {selectedOrgId
                            ? 'Select site'
                            : 'Select an organization first'}
                        </option>
                        {siteOptions.map(l => (
                          <option key={l.id} value={String(l.id)}>
                            {l.name}
                          </option>
                        ))}
                      </select>
                      {selectedOrgId && siteOptions.length === 0 && (
                        <p className='text-[11px] text-orange-500 mt-1'>
                          No sites in this organization yet — add one in
                          Organization management.
                        </p>
                      )}
                    </div>

                    {/* Department */}
                    <div>
                      <label className='block text-[13px] font-semibold text-[#334155] mb-1.5'>
                        Department
                      </label>
                      <select
                        value={selectedDeptId}
                        onChange={e => setSelectedDeptId(e.target.value)}
                        disabled={!effectiveLocationId}
                        className='w-full border border-gray-300 p-2.5 rounded-md focus:ring-1 focus:ring-blue-600 outline-none text-sm bg-white disabled:bg-gray-50 disabled:text-gray-400'
                      >
                        <option value=''>
                          {effectiveLocationId
                            ? 'Select department'
                            : 'Select a site first'}
                        </option>
                        {deptOptions.map(d => (
                          <option key={d.id} value={String(d.id)}>
                            {d.name}
                          </option>
                        ))}
                      </select>
                      {effectiveLocationId && deptOptions.length === 0 && (
                        <p className='text-[11px] text-orange-500 mt-1'>
                          No departments in this site yet — add one in
                          Organization management.
                        </p>
                      )}
                    </div>
                  </>
                )}

                {/* LOCATION (internal placement — free text, optional) */}
                <div>
                  <label className='block text-[13px] font-semibold text-[#334155] mb-1.5'>
                    Location
                    <span className='ml-1 font-normal text-gray-400 text-[11px]'>
                      where it sits inside the department — optional
                    </span>
                  </label>
                  <input
                    type='text'
                    name='placement'
                    value={formData.placement}
                    onChange={handleInputChange}
                    placeholder=''
                    className='w-full border border-gray-300 p-2.5 rounded-md focus:ring-1 focus:ring-blue-600 outline-none text-sm'
                  />
                </div>

                {/* STATUS */}
                <div>
                  <label className='block text-[13px] font-semibold mb-1.5'>
                    Status
                  </label>
                  <select
                    name='status'
                    value={formData.status}
                    onChange={handleInputChange}
                    className='w-full border p-2.5 rounded-md'
                  >
                    <option value='Active'>Active</option>
                    <option value='Maintenance'>Maintenance</option>
                    <option value='Faulty'>Faulty</option>
                  </select>
                </div>

                {/* DATE */}
                <div>
                  <label className='block text-[13px] font-semibold mb-1.5'>
                    Next Maintenance Date
                  </label>
                  <input
                    type='date'
                    name='next_maintenance_date'
                    value={formData.next_maintenance_date}
                    onChange={handleInputChange}
                    className='w-full border p-2.5 rounded-md'
                  />
                </div>

                {/* ACTION */}
                <div className='flex justify-end gap-4 mt-4'>
                  <button
                    type='button'
                    onClick={() => setIsFormModalOpen(false)}
                    className='text-gray-500'
                  >
                    Cancel
                  </button>
                  <button className='bg-blue-600 text-white px-5 py-2 rounded'>
                    {editingId ? 'Update' : 'Save'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* =========================================
          2. DELETE CONFIRMATION DIALOG
          ========================================= */}
      {isDeleteModalOpen && (
        <div className='fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50'>
          <div className='bg-white p-6 rounded-xl w-80 shadow-2xl text-center'>
            <h3 className='text-lg font-bold text-gray-900 mb-2'>
              Delete Asset
            </h3>
            <p className='text-sm text-gray-500 mb-6'>
              Are you sure you want to delete{' '}
              <span className='font-semibold text-gray-800'>
                "{assetToDelete?.name}"
              </span>
              ?
            </p>
            <div className='flex gap-3 w-full'>
              <button
                onClick={() => setIsDeleteModalOpen(false)}
                className='flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium text-sm'
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className='flex-1 px-4 py-2 bg-[#ff6b6b] text-white rounded-lg hover:bg-red-600 transition-colors font-medium text-sm'
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* =========================================
          3. TOAST NOTIFICATION
          ========================================= */}
      {toast.visible && (
        <div
          className={`fixed bottom-6 right-6 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 transform transition-all duration-300 z-50
          ${
            toast.type === 'success'
              ? 'bg-green-50 border-l-4 border-green-500 text-green-800'
              : 'bg-red-50 border-l-4 border-red-500 text-red-800'
          }`}
        >
          {toast.type === 'success' ? (
            <svg
              className='w-5 h-5 text-green-500'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth='2'
                d='M5 13l4 4L19 7'
              ></path>
            </svg>
          ) : (
            <svg
              className='w-5 h-5 text-red-500'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth='2'
                d='M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
              ></path>
            </svg>
          )}
          <span className='text-sm font-medium'>{toast.message}</span>
        </div>
      )}
    </div>
  )
}
